// src/components/dialog/DlgSensorUserAssignments.tsx

import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  useTheme,
} from '@mui/material'
import { useEffect, useState } from 'react'

import { SensorId } from '@/types/domain'
import { UserId, UserPool } from '@/types/domain/user'
import { KARMA_BasicDialog } from '@/types/karma/dialog'

import { SORT_ATTR_FIRSTNAME } from '../../api'
import { BtnAddAllSensors, BtnRemoveAllSensors, HStack } from '../../components'
import { useUserPool } from '../../hooks'

type DlgSensorUserAssignmentsProps = KARMA_BasicDialog & {
  data: { id: SensorId; userIds: UserId[] }
  onChange: (userIds: UserId[]) => void
}

export const DlgSensorUserAssignments = ({
  data,
  onChange,
  onClose,
  open,
}: DlgSensorUserAssignmentsProps) => {
  const { id, userIds } = data

  const theme = useTheme()
  const { white: color } = theme.palette

  const uPool: UserPool = useUserPool()

  const [selected, setSelected] = useState<UserId[]>([])

  useEffect(() => setSelected(userIds), [userIds])

  const addAll = () => setSelected(uPool.map(u => Number(u.id)))
  const removeAll = () => setSelected([])

  return (
    <Dialog maxWidth={'md'} open={open} onClose={onClose}>
      <DialogTitle>Benutzerzuweisung für Sensor ID {id}</DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <List sx={{ maxHeight: 500, overflow: 'auto' }}>
          {uPool.sort(SORT_ATTR_FIRSTNAME).map(user => {
            const { firstname, label, lastname } = user.attributes
            const userId = Number(user.id)
            const isSelected = Boolean(selected.find(f => f === userId))
            const fullName = `${firstname} ${lastname}`
            const userIdStr = `ID: ${user.id}`

            const onClick = () => {
              selected.includes(Number(user.id))
                ? setSelected(selected.filter(f => f !== userId))
                : setSelected([...selected, userId])

              if (!isSelected) {
                const users = [...selected, userId]

                setSelected(users)
                onChange(users)
              } else {
                const users = selected.filter(f => f !== userId)

                setSelected(users)
                onChange(users)
              }
            }

            return (
              <ListItemButton key={userId} onClick={onClick}>
                <ListItemText
                  primary={fullName}
                  secondary={label}
                  sx={{ width: 0.7 }}
                />

                <ListItemText primary={userIdStr} sx={{ width: 0.3 }} />

                <Checkbox edge={'end'} checked={isSelected} />
              </ListItemButton>
            )
          })}
        </List>
      </DialogContent>

      <DialogActions>
        <HStack sx={{ justifyContent: 'space-between', mx: 1, width: 1 }}>
          {selected.length} Benutzer zugewiesen
          <HStack>
            <BtnRemoveAllSensors onClick={removeAll} sx={{ color, ml: 5 }} />
            <BtnAddAllSensors onClick={addAll} sx={{ color }} />
          </HStack>
        </HStack>
      </DialogActions>
    </Dialog>
  )
}
